import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, endpoints } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const INTERESTS = ["Education","Environment","Health","Disaster Relief","Women's Empowerment","Youth Leadership","Digital Literacy","Consumer Rights","Legal Awareness","Rural Development"];

export const VolunteerForm = ({ compact = false }) => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", city: "", state: "", age: "", profession: "", availability: "Weekends", interests: [], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggleInterest = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age, 10) : undefined };
      await api.post(endpoints.volunteers, payload);
      setDone(true);
      toast.success("Thank you! Our team will reach out within 48 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="volunteer-form-success" className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-8 text-center">
      <CheckCircle2 className="h-10 w-10 text-[hsl(var(--green))] mx-auto" />
      <h3 className="heading-3 mt-3">You’re in. Welcome to CAF.</h3>
      <p className="lead mt-2">We’ve received your details. A volunteer coordinator will contact you within 48 hours with your onboarding path.</p>
    </div>
  );

  return (
    <form onSubmit={onSubmit} className={`grid gap-4 ${compact ? "" : "lg:grid-cols-2"}`} data-testid="volunteer-form">
      <Field label="Full name*" ><Input required data-testid="volunteer-form-full_name" value={form.full_name} onChange={set("full_name")} /></Field>
      <Field label="Email*"><Input required type="email" data-testid="volunteer-form-email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Phone*"><Input required data-testid="volunteer-form-phone" value={form.phone} onChange={set("phone")} /></Field>
      <Field label="City*"><Input required data-testid="volunteer-form-city" value={form.city} onChange={set("city")} /></Field>
      <Field label="State*"><Input required data-testid="volunteer-form-state" value={form.state} onChange={set("state")} /></Field>
      <Field label="Age"><Input type="number" min="13" max="99" data-testid="volunteer-form-age" value={form.age} onChange={set("age")} /></Field>
      <Field label="Profession"><Input data-testid="volunteer-form-profession" value={form.profession} onChange={set("profession")} /></Field>
      <Field label="Availability">
        <Select value={form.availability} onValueChange={set("availability")}>
          <SelectTrigger data-testid="volunteer-form-availability"><SelectValue placeholder="Select availability" /></SelectTrigger>
          <SelectContent>
            {["Weekends","Weekday evenings","Full-time","Remote-only","Event-based"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
          </SelectContent>
        </Select>
      </Field>
      <div className="lg:col-span-2">
        <Label className="text-sm font-medium">Interests</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {INTERESTS.map((i) => (
            <label key={i} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs cursor-pointer ${form.interests.includes(i) ? "border-[hsl(var(--navy))] bg-[hsl(var(--surface-tint))] text-[hsl(var(--navy))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-ink))] hover:border-[hsl(var(--navy))]"}`}>
              <Checkbox data-testid={`volunteer-form-interest-${i.toLowerCase().replace(/[^a-z]/g,"-")}`} checked={form.interests.includes(i)} onCheckedChange={() => toggleInterest(i)} className="h-3.5 w-3.5" />
              {i}
            </label>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2">
        <Field label="Anything you’d like us to know?"><Textarea data-testid="volunteer-form-message" value={form.message} onChange={set("message")} rows={3} placeholder="Prior volunteering, skills, causes you care about..."/></Field>
      </div>
      <div className="lg:col-span-2 flex items-center justify-between gap-3">
        <p className="text-xs text-[hsl(var(--muted-ink))]">By submitting, you agree to our privacy policy.</p>
        <Button disabled={loading} type="submit" data-testid="volunteer-form-submit" className="bg-[hsl(var(--navy))] hover:bg-[hsl(var(--navy-2))] text-background">{loading ? "Submitting..." : "Submit application"}</Button>
      </div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-[hsl(var(--navy))]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
